import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import {
  stringToDate,
  toDisplayDate
} from '@databases/utils/database/field-types/date-presentation';
import { createElement, type ReactNode } from 'react';

import { defineFieldReadModule, type FieldReadModule } from '../field-read-module.ts';
import { EMPTY_DATE_CONTENT, type DateReadContent } from './date-read-content.ts';
import { DateValueView } from './date-value-view.tsx';

function createDateContent(
  value: string | undefined,
  locale: string,
  timeFormat: DatabaseField['settings']['timeFormat']
): DateReadContent {
  if (!value) {
    return EMPTY_DATE_CONTENT;
  }

  let text = value;

  try {
    text = toDisplayDate(value, locale, timeFormat) ?? value;
  } catch {
    return Object.freeze<DateReadContent>({ kind: 'date', text: value });
  }

  return Object.freeze<DateReadContent>({ kind: 'date', text });
}

export type DateReadRenderer = (content: DateReadContent) => ReactNode;

const renderDateValue: DateReadRenderer = (content) =>
  createElement(DateValueView, { content });

/**
 * Creates Date's locale- and experiment-aware, read-only field module.
 */
export function createDateFieldReadModule({
  locale,
  dateFieldSettingsEnabled,
  render = renderDateValue
}: {
  readonly locale: string;
  readonly dateFieldSettingsEnabled: boolean;
  readonly render?: DateReadRenderer;
}): FieldReadModule<DateReadContent> {
  return defineFieldReadModule<DateReadContent>({
    fieldType: DatabaseFieldType.DATE,
    render,
    compile(field: DatabaseField) {
      const timeFormat = dateFieldSettingsEnabled ? field.settings.timeFormat : undefined;

      return Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ): DateReadContent {
          const value = storedValue ?? defaultValue;

          if (value?.type === DatabaseFieldType.DATE) {
            return createDateContent(value.value, locale, timeFormat);
          }

          if (value?.type === DatabaseFieldType.TEXT) {
            return createDateContent(stringToDate(value.value.plain), locale, timeFormat);
          }

          return EMPTY_DATE_CONTENT;
        }
      });
    }
  });
}
